import { ActionAfterViewObserver } from '@cian/action-after-viewed-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getDateRangeValue, getRangeValue } from '../../../../packages/JsonQuery';
import { onAddToFavorite as addToFavorites } from '../../actions/newFavorites/onAddToFavorite';
import { removeFromFavorites } from '../../actions/newFavorites/removeFromFavorite';
import { FavoriteButton } from '../../components/FavoriteButton';
import { OfferCard } from '../../components/OfferCard';
import { useApplicationContext } from '../../containers/ApplicationContext';
import { useTrackingProps } from '../../hooks/useTrackingProps';
import { IDailyRentOfferForMobileSite } from '../../repositories/dailyrent/entities/daily_rent_offers_for_web/DailyRentOfferForMobileSite';
import { selectMlSearchSessionGuid } from '../../selectors/analytics';
import { selectListingMlRanking } from '../../selectors/listingOffers/selectListingMlRanking';
import { selectResultsJsonQuery } from '../../selectors/results';
import {
  trackCardClick,
  trackAddFavorite,
  trackBookClick,
  trackCall,
  trackChatClick,
  trackShowPhoneClick,
  trackSlideChange,
} from '../../tracking';
import { trackBuildingClick } from '../../tracking/trackBuildingClick';
import { trackMoreClick } from '../../tracking/trackMoreClick';
import { trackOfferCardShow } from '../../tracking/trackOfferCardShow';
import { trackRemoveFromFavorite } from '../../tracking/trackRemoveFromFavorite';
import { TThunkDispatch } from '../../types/redux';
import { getOfferLinkRel } from '../../utils/getOfferLinkRel';
import { prepareOfferUrl } from '../../utils/prepareOfferUrl';
import { defferedTrack } from '../../utils/tracking';

export interface IOfferCardMobileContainerProps {
  offer: IDailyRentOfferForMobileSite;
  offerPosition: number;
  offersLength: number;
}

export const OfferCardMobileContainer = ({ offer, offerPosition, offersLength }: IOfferCardMobileContainerProps) => {
  const { config } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const mlRanking = useSelector(selectListingMlRanking);
  const mlSearchSessionGuid = useSelector(selectMlSearchSessionGuid);
  const isSliderEventTriggeredRef = React.useRef(false);
  const isPartnerFavoritesEnabled = !!config.get<boolean>('dailyrent.partners_favorites.Enabled');
  const jsonQueryDates = getDateRangeValue('dates')(jsonQuery);
  const checkInDate = jsonQueryDates?.gte;
  const checkOutDate = jsonQueryDates?.lt;
  const minBeds = getRangeValue('beds_count')(jsonQuery)?.gte;
  const isFavoriteButtonVisible = (!offer.isCianPartner || isPartnerFavoritesEnabled) && !offer.isHotel;
  const hasDates = !!jsonQueryDates;

  const offerLink = prepareOfferUrl({
    offer,
    checkInDate,
    checkOutDate,
    minBeds,
  });
  const offerLinkRel = getOfferLinkRel(offer);
  const trackingProps = useTrackingProps({ offer, offerPosition, offersLength, reason: 'all_objects_on_map' });

  const onCardClick = React.useCallback(() => {
    trackCardClick(trackingProps);
  }, [trackingProps]);

  const onMoreClick = React.useCallback(() => {
    trackMoreClick(trackingProps);
  }, [trackingProps]);

  const onBookClick = React.useCallback(() => {
    trackBookClick(trackingProps);
  }, [trackingProps]);

  const onCallClick = React.useCallback(() => {
    trackShowPhoneClick(trackingProps);
    defferedTrack(() => trackCall(trackingProps));
  }, [trackingProps]);

  const onChatClick = React.useCallback(() => {
    trackChatClick(trackingProps);
  }, [trackingProps]);

  const onSlideChange = React.useCallback(() => {
    if (!isSliderEventTriggeredRef.current) {
      trackSlideChange(trackingProps);
      isSliderEventTriggeredRef.current = true;
    }
  }, [trackingProps]);

  const onBuildingClick = React.useCallback(() => {
    trackBuildingClick(trackingProps);
  }, [trackingProps]);

  const onAddToFavorites = React.useCallback(() => {
    dispatch(addToFavorites(offer));
    trackAddFavorite(trackingProps);
  }, [dispatch, offer, trackingProps]);

  const onRemoveFromFavorites = React.useCallback(() => {
    dispatch(removeFromFavorites(offer));
    trackRemoveFromFavorite(trackingProps);
  }, [dispatch, offer, trackingProps]);

  const onCardScrollIntoView = () => {
    trackOfferCardShow(trackingProps, mlRanking, mlSearchSessionGuid);
  };

  const carouselControls = React.useMemo(
    () =>
      isFavoriteButtonVisible ? (
        <FavoriteButton isFavorite={offer.isFavorite} onAdd={onAddToFavorites} onRemove={onRemoveFromFavorites} />
      ) : null,
    [isFavoriteButtonVisible, offer.isFavorite, onAddToFavorites, onRemoveFromFavorites],
  );

  return (
    <ActionAfterViewObserver
      percentVisible={0.5}
      callback={onCardScrollIntoView}
      triggerOnce={false}
      callbackDelay={1000}
    >
      <OfferCard
        offer={offer}
        hasDates={hasDates}
        // Ранее отображался для EBrandingLevel.ThirdLevel и партнеров
        showBrandingBlock={false}
        showThumbnails={true}
        carouselControls={carouselControls}
        isSingle={false}
        offerLink={offerLink}
        offerLinkRel={offerLinkRel}
        onCardClick={onCardClick}
        onMoreClick={onMoreClick}
        onBookClick={onBookClick}
        onCallClick={onCallClick}
        onChatClick={onChatClick}
        onSlideChange={onSlideChange}
        onBuildingClick={onBuildingClick}
      />
    </ActionAfterViewObserver>
  );
};
